import {
  Pagination,
  Spinner,
  Table,
  TableBody,
  TableDataCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from '@consigli/facade';
import { useInvites, useMyUser } from '@consigli/hooks';
import { FC, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

export const UserManagementInviteList: FC = () => {
  const pageSize = 25;
  const { user } = useMyUser();
  const { t } = useTranslation();
  const [page, setPage] = useState(1);

  const { invites, paginatedCount, isFetching } = useInvites({
    page: page,
    pageSize: pageSize,
    invitedByEmail: !user.isSuperuser ? user.email : '',
  });

  const formatDate = useCallback((isoDateString: string): string => {
    const date = new Date(isoDateString);
    return date.toLocaleDateString(undefined);
  }, []);

  const handlePageChange = useCallback(
    (page: number) => {
      setPage(page);
    },
    [setPage],
  );

  return (
    <>
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm max-h-[calc(100vh-310px)] overflow-y-auto">
        <Table>
          <TableHead className="sticky top-0 bg-day-light-4">
            <TableRow className="border-b border-gray-100">
              <TableHeaderCell className="pl-6 pr-8 py-3 text-sm font-semibold text-day-neutral-dark">
                {t('user-management.recipient')}
              </TableHeaderCell>
              <TableHeaderCell className="pr-8 py-3 text-sm font-semibold text-day-neutral-dark">
                {t('user-management.status')}
              </TableHeaderCell>
              <TableHeaderCell className="pr-6 py-3 text-sm font-semibold text-day-neutral-dark">
                {t('user-management.expires-at')}
              </TableHeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isFetching ? (
              <TableRow>
                <TableDataCell colSpan={3} className="py-12">
                  <div className="w-full flex justify-center">
                    <Spinner />
                  </div>
                </TableDataCell>
              </TableRow>
            ) : (
              invites.map((invite, index) => (
                <TableRow
                  key={invite.id}
                  className={index !== invites.length - 1 ? 'border-b border-gray-100' : ''}
                >
                  <TableDataCell className="pl-6 pr-8 py-2.5 text-sm font-medium text-day-neutral-dark truncate max-w-[25vw]">
                    {invite.recipient}
                  </TableDataCell>
                  <TableDataCell className="pr-8 py-2.5 text-sm text-day-neutral-subtle truncate max-w-[25vw]">
                    {t(`user-management.invite-status.${invite.status.toLowerCase()}`)}
                  </TableDataCell>
                  <TableDataCell className="pr-6 py-2.5 text-sm text-day-neutral-subtle truncate max-w-[25vw]">
                    {formatDate(invite.expiresAt)}
                  </TableDataCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
      <div className="mt-4 relative flex items-center justify-center">
        <p className="absolute left-0 font-bold text-sm text-day-neutral-subtle">
          {paginatedCount} {t('user-management.invites')}
        </p>
        <Pagination
          currentPage={page}
          totalCount={paginatedCount || 0}
          pageSize={pageSize}
          onPageChange={handlePageChange}
        />
      </div>
    </>
  );
};
